import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect } from "react";
import HomePage from "@/pages/HomePage";
import ResultsPage from "@/pages/ResultsPage";
import SEOPage from "@/pages/SEOPage";
import TellMeAboutYourselfPage from "@/pages/TellMeAboutYourselfPage";
import HowToPresentExperiencePage from "@/pages/HowToPresentExperiencePage";
import AppDashboard from "@/pages/AppDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSettings from "@/pages/AdminSettings";
import AuthCallback from "@/pages/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import InsightsPage from "@/pages/InsightsPage";

// Public Content Pages (AdSense Compliance)
import GuidesIndexPage from "@/pages/GuidesIndexPage";
import InterviewPreparationGuidePage from "@/pages/guides/InterviewPreparationGuidePage";
import JobDescriptionAnalysisGuidePage from "@/pages/guides/JobDescriptionAnalysisGuidePage";
import SeniorInterviewQuestionsGuidePage from "@/pages/guides/SeniorInterviewQuestionsGuidePage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import ExecutiveInterviewBlogPage from "@/pages/blog/ExecutiveInterviewBlogPage";
import DynamicBlogPost from "@/pages/blog/DynamicBlogPost";
import ContactPage from "@/pages/ContactPage";
import PricingPage from "@/pages/PricingPage";
import TermsPage from "@/pages/TermsPage";

import CookieConsent from "@/components/CookieConsent";
import ImpersonationBanner from "@/components/ImpersonationBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [impersonationData, setImpersonationData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if this is an OAuth callback with session_id (legacy)
  const isOAuthCallback = location.hash?.includes('session_id=');
  
  // Check if this is a cross-domain OAuth redirect with session_token in URL
  const searchParams = new URLSearchParams(location.search);
  const sessionTokenFromUrl = searchParams.get('session_token');
  const impersonationTokenFromUrl = searchParams.get('impersonation_token');

  // Handle exit impersonation
  const handleExitImpersonation = () => {
    localStorage.removeItem('hireun_impersonation');
    localStorage.removeItem('hireun_impersonation_token');
    setImpersonationData(null);
    // Redirect to admin dashboard
    window.location.href = '/admin';
  };

  // If user data was passed from AuthCallback, use it directly
  useEffect(() => {
    // Handle impersonation token from URL (admin initiated support mode)
    if (impersonationTokenFromUrl) {
      const handleImpersonationToken = async () => {
        try {
          // Validate the impersonation token
          const response = await fetch(`${API}/admin/impersonate/validate?token=${impersonationTokenFromUrl}`, {
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('hireun_session_token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            // Store impersonation data
            localStorage.setItem('hireun_impersonation', JSON.stringify(data));
            localStorage.setItem('hireun_impersonation_token', impersonationTokenFromUrl);
            setImpersonationData(data);
            setUser(data.target_user);
            setIsAuthenticated(true);
            // Clean URL
            navigate(location.pathname, { replace: true });
          } else {
            console.error('Invalid impersonation token');
            navigate('/admin', { replace: true });
          }
        } catch (error) {
          console.error('Impersonation validation failed:', error);
          navigate('/admin', { replace: true });
        }
      };
      handleImpersonationToken();
      return;
    }

    // Check for existing impersonation session
    const storedImpersonation = localStorage.getItem('hireun_impersonation');
    const storedImpersonationToken = localStorage.getItem('hireun_impersonation_token');
    if (storedImpersonation && storedImpersonationToken) {
      try {
        const impData = JSON.parse(storedImpersonation);
        // Check if session is still valid (not expired)
        const expiresAt = new Date(impData.expires_at);
        if (expiresAt > new Date()) {
          setImpersonationData(impData);
          setUser(impData.target_user);
          setIsAuthenticated(true);
          return;
        } else {
          // Session expired, clean up
          localStorage.removeItem('hireun_impersonation');
          localStorage.removeItem('hireun_impersonation_token');
        }
      } catch (e) {
        localStorage.removeItem('hireun_impersonation');
        localStorage.removeItem('hireun_impersonation_token');
      }
    }

    // Handle cross-domain OAuth redirect with session_token in URL
    if (sessionTokenFromUrl) {
      // Store token in cookie via a backend call, then clean URL
      const handleTokenFromUrl = async () => {
        try {
          // Verify the token and get user data
          const response = await fetch(`${API}/auth/me`, {
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${sessionTokenFromUrl}`
            }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setIsAuthenticated(true);
            localStorage.setItem('hireun_user', JSON.stringify(userData));
            localStorage.setItem('hireun_session_token', sessionTokenFromUrl);
            // Clean the URL by removing the token
            navigate(location.pathname, { replace: true });
          } else {
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          setIsAuthenticated(false);
        }
      };
      handleTokenFromUrl();
      return;
    }
    
    // Skip auth check if this is an OAuth callback
    if (isOAuthCallback) {
      return;
    }
    
    // Check if user data was passed in navigation state
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }
    
    // Check localStorage for cached user data
    const cachedUser = localStorage.getItem('hireun_user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        setIsAuthenticated(true);
        return;
      } catch (e) {
        localStorage.removeItem('hireun_user');
      }
    }

    // Otherwise check auth status via API
    const checkAuth = async () => {
      try {
        // Use stored token if available
        const storedToken = localStorage.getItem('hireun_session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        // Cache user data
        localStorage.setItem('hireun_user', JSON.stringify(userData));
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('hireun_user');
        localStorage.removeItem('hireun_session_token');
      }
    };
    checkAuth();
  }, [location.state, isOAuthCallback, sessionTokenFromUrl, impersonationTokenFromUrl, navigate, location.pathname]);

  // CRITICAL: If OAuth callback, render AuthCallback immediately
  if (isOAuthCallback) {
    return <AuthCallback />;
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin-only routes
  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/app" replace />;
  }

  // Render with impersonation banner if in impersonation mode
  const content = typeof children === 'function' ? children({ user, impersonationData }) : children;
  
  if (impersonationData) {
    return (
      <>
        <ImpersonationBanner 
          impersonationData={impersonationData}
          onExitImpersonation={handleExitImpersonation}
          impersonationToken={localStorage.getItem('hireun_impersonation_token')}
        />
        <div style={{ paddingTop: '52px' }}>
          {content}
        </div>
      </>
    );
  }

  return content;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render (not in useEffect)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/results" element={<ResultsPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/interview-questions/:role" element={<SEOPage />} />
      <Route path="/tell-me-about-yourself-interview" element={<TellMeAboutYourselfPage />} />
      <Route path="/how-to-present-your-experience" element={<HowToPresentExperiencePage />} />
      
      {/* Public Content Pages (AdSense Compliance) */}
      <Route path="/guides" element={<GuidesIndexPage />} />
      <Route path="/guides/interview-preparation" element={<InterviewPreparationGuidePage />} />
      <Route path="/guides/job-description-analysis" element={<JobDescriptionAnalysisGuidePage />} />
      <Route path="/guides/senior-interview-questions" element={<SeniorInterviewQuestionsGuidePage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/how-to-prepare-executive-interview" element={<ExecutiveInterviewBlogPage />} />
      <Route path="/blog/:slug" element={<DynamicBlogPost />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      
      {/* Protected Routes */}
      <Route 
        path="/app" 
        element={
          <ProtectedRoute>
            {({ user, impersonationData }) => <AppDashboard user={user} impersonationData={impersonationData} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/session/:sessionId" 
        element={
          <ProtectedRoute>
            {({ user, impersonationData }) => <AppDashboard user={user} impersonationData={impersonationData} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute adminOnly={true}>
            {({ user }) => <AdminDashboard user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/settings" 
        element={
          <ProtectedRoute adminOnly={true}>
            {({ user }) => <AdminSettings user={user} />}
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppRouter />
        <CookieConsent />
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
